import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@environment/environment';
import { Observable, catchError, map, throwError } from 'rxjs';

/**
 * Cliente HTTP fino de la API REST de doble factor de `Bp.Identity.Server` (feature 056, T034).
 * Contrato: `specs/056-doble-factor-opcional/contracts/identity-two-factor-api.md`.
 *
 * El Bearer lo agrega el `authInterceptor` funcional de bp-admin (`app/core/auth/auth.interceptor.ts`),
 * que se aplica a TODA petición del `HttpClient` raíz, igual que en `IdentityService`. No se usa gRPC:
 * el doble factor vive en Identity (research R-12).
 */

/** Métodos que puede devolver Identity, en este orden. */
export type TwoFactorMethod = 'authenticator' | 'email' | 'passkey';

/** `GET /api/TwoFactor/me` — estado propio (contrato §1). */
export interface TwoFactorStatus {
  enabled: boolean;
  methods: TwoFactorMethod[];
  passkeyCount: number;
  recoveryCodesLeft: number;
  requiredByRole: boolean;
  /** URL base de «Seguridad»; la SPA le agrega `?returnUrl=`. */
  securityUrl: string;
}

/** `GET /api/TwoFactor/users/{userId}` — estado de otra persona (contrato §2). */
export interface TwoFactorUserStatus {
  /** Id de masterDb. */
  userId: number;
  enabled: boolean;
  methods: TwoFactorMethod[];
  passkeyCount: number;
  recoveryCodesLeft: number;
  /** ISO 8601. Nulo si nunca lo activó. */
  enabledAt: string | null;
  canReset: boolean;
  /** Traza para soporte (contrato §2): una entrada por cada valor de `methods`, en el mismo orden. */
  methodDetails: TwoFactorMethodDetail[];
  /** ISO 8601 del juego de códigos de respaldo vigente. Nulo si no hay doble factor o no hay evento. */
  recoveryCodesSince: string | null;
  /** Último restablecimiento por soporte. Nulo si nunca; también viene con `enabled = false`. */
  lastReset: TwoFactorLastReset | null;
}

/** Desde cuándo está activo un método vigente (contrato §2, `methodDetails`). */
export interface TwoFactorMethodDetail {
  method: TwoFactorMethod;
  /** ISO 8601. */
  since: string | null;
}

/** App desde la que se restableció, según `origin_client_id`; nulo si no calza con ninguna. */
export type TwoFactorResetApp = 'bp-admin' | 'bp-advisor';

/** Último restablecimiento por soporte (contrato §2, `lastReset`). */
export interface TwoFactorLastReset {
  /** ISO 8601. */
  at: string | null;
  /** Nulo si el actor ya no existe. */
  byName: string | null;
  byEmail: string | null;
  byApp: TwoFactorResetApp | null;
  reason: string;
}

/** Cuerpo de error que arma `BpExceptionFilter`. */
export interface TwoFactorErrorBody {
  errorCode: string;
  message: string;
}

/** Códigos de error del contrato (tabla «Códigos de error»). */
export const TWO_FACTOR_ERROR_CODES = {
  forbidden: 'BP-ID-2FA-00010',
  notFound: 'BP-ID-2FA-00011',
  invalidReason: 'BP-ID-2FA-00012',
  notEnabled: 'BP-ID-2FA-00013',
} as const;

/** Total de códigos de respaldo que se generan (contrato §1: `recoveryCodesLeft` va de 0 a 10). */
export const TWO_FACTOR_RECOVERY_CODES_TOTAL = 10;

/** Largo del motivo del restablecimiento, medido después de `trim()` (contrato §3). */
export const TWO_FACTOR_RESET_REASON_MIN = 5;
export const TWO_FACTOR_RESET_REASON_MAX = 500;

/**
 * Error normalizado de la API. `status` 0 = sin respuesta (red, CORS).
 * `errorCode` y `message` vienen del cuerpo `{ errorCode, message }` cuando el servidor lo manda.
 */
export class TwoFactorApiError extends Error {
  constructor(
    readonly status: number,
    readonly errorCode: string | null,
    readonly displayMessage: string | null,
  ) {
    super(displayMessage ?? `TwoFactor API error (HTTP ${status})`);
    this.name = 'TwoFactorApiError';
  }

  static from(error: unknown): TwoFactorApiError {
    if (error instanceof TwoFactorApiError) {
      return error;
    }
    if (error instanceof HttpErrorResponse) {
      const body = TwoFactorApiError.parseBody(error.error);
      return new TwoFactorApiError(error.status, body?.errorCode ?? null, body?.message ?? null);
    }
    return new TwoFactorApiError(0, null, null);
  }

  private static parseBody(raw: unknown): Partial<TwoFactorErrorBody> | null {
    if (!raw) {
      return null;
    }
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw) as Partial<TwoFactorErrorBody>;
      } catch {
        return null;
      }
    }
    if (typeof raw === 'object') {
      return raw as Partial<TwoFactorErrorBody>;
    }
    return null;
  }
}

@Injectable({
  providedIn: 'root',
})
export class IdentityTwoFactorService {
  private readonly http = inject(HttpClient);

  private get baseUrl(): string {
    return `${environment.auth.issuer}/api/TwoFactor`;
  }

  /** Estado del doble factor de quien está logueado (menú de usuario, B13). */
  getMe(): Observable<TwoFactorStatus> {
    return this.http
      .get<TwoFactorStatus>(`${this.baseUrl}/me`)
      .pipe(catchError(error => throwError(() => TwoFactorApiError.from(error))));
  }

  /** Estado del doble factor de otra persona, por id de masterDb (ficha del usuario, C14). */
  getUser(userId: number): Observable<TwoFactorUserStatus> {
    return this.http
      .get<TwoFactorUserStatus>(`${this.baseUrl}/users/${encodeURIComponent(String(userId))}`)
      .pipe(catchError(error => throwError(() => TwoFactorApiError.from(error))));
  }

  /**
   * Restablece el doble factor de otra persona. 204 → completa sin valor.
   * El motivo viaja recortado: el servidor valida 5 a 500 caracteres después de `Trim()`.
   */
  reset(userId: number, reason: string): Observable<void> {
    return this.http
      .post<void>(`${this.baseUrl}/users/${encodeURIComponent(String(userId))}/reset`, {
        reason: (reason ?? '').trim(),
      })
      .pipe(
        map(() => undefined),
        catchError(error => throwError(() => TwoFactorApiError.from(error))),
      );
  }
}
